import { DatePipe, DecimalPipe, Location } from "@angular/common";
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { DateTime } from "luxon";
import { of, throwError } from "rxjs";
import { catchError, flatMap, map } from "rxjs/operators";
import { conformToMask } from "text-mask-core";
import { GroupsEnum } from "../../enum/groups.enum";

import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import { Bank } from "../../model/domain/Bank";
import {
  DialogSize,
  MarketType,
  OperationType,
  Portfolio,
  TicketStatus,
  TicketType,
} from "../../model/domain/Entity";
import { Margin } from "../../model/domain/Margin";
import { PublicSecurity } from "../../model/domain/PublicSecurity";
import { NamedType } from "../../model/domain/Tipo";
import { AuthService } from "../../services/auth.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { DomainService } from "../../services/domain.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { CounterpartComponent } from "../counterpart/counterpart.component";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";
import { DadosFundoComponent } from "../dados-fundo/dados-fundo.component";

@Component({
  selector: "app-margem",
  templateUrl: "./margem.component.html",
  styleUrls: ["./margem.component.css"],
})
export class MargemComponent implements OnInit, OnDestroy {
  faInfoCircle = faInfoCircle;
  public groups = GroupsEnum;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: TicketService,
    public validator: ValidatorService,
    private dialogService: DialogService,
    private authService: AuthService,
    private snackBarService: SnackbarService,
    private datePipe: DatePipe,
    private location: Location,
    private loaderService: LoaderService,
    private cutoffService: CutoffService,
    private domainService: DomainService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        this.resetForm();
      }
    });
  }

  @ViewChild("thisForm", { static: true }) public frm: NgForm;
  @ViewChild("portfolio", { static: true }) fundoComponent: DadosFundoComponent;
  @ViewChild("counterpart", { static: true })
  counterpartComponent: CounterpartComponent;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;

  TicketTypeEnum = TicketType;

  dataChanged: EventEmitter<string>;

  navigationSubscription;

  model: Margin;
  banksList: Bank[];
  TiposCompra: NamedType<number>[];
  TiposMercado: NamedType<number>[];
  TiposLiquidacao: NamedType<number>[];

  isEditing = false;
  canValidate = false;
  ngForm: NgForm;

  numberMask: any;

  decimalPipe = new DecimalPipe("pt");

  validators = {};

  publicSecurityTypes: string[];
  selectedPublicSecurity: string = null;

  publicSecurityIdsList: string[] = null;
  selectedPublicSecurityId: string = null;

  publicSecurityListResult: PublicSecurity[] = null;

  publicSecurityList: PublicSecurity[] = null;
  selectedPublicSecurityItem: PublicSecurity = null;
  isNotProduction = environment.name != 'production';

  validatorsInit() {
    // TODO: utilizar um validador já existente, talvez uma diretiva do próprio angular
    this.validators = {
      marketTypeId: (value) =>
        this.TiposMercado.map((tm) => tm.id).includes(value),
      amount: (value) =>
        this.coalesce(value).length === 0 ||
        conformToMask(value, this.validator.decimalIndexFeeMask, {}),
      unitPrice: (value) =>
        this.coalesce(value).length === 0 ||
        conformToMask(value, this.validator.unitPriceMask, {}),
      operationValue: (value) =>
        this.coalesce(value).length === 0 ||
        conformToMask(value, this.validator.operationValueMask, {}),
      quotation: (value) =>
        this.coalesce(value).length === 0 ||
        conformToMask(value, this.validator.quotationMask, {}),
      purchaseDate: (value) =>
        this.isRFTituloPublico() || this.isRFTituloPrivado()
          ? this.model.purchaseDate != null && value != ""
          : this.model.purchaseDate == null,
      expirationDate: (value) =>
        this.isRFTituloPublico() || this.isRFTituloPrivado()
          ? this.model.expirationDate != null && value != ""
          : this.model.expirationDate == null,
      securityCode: (value) =>
        this.isRFTituloPublico() || this.isRFTituloPrivado()
          ? this.coalesce(value).length > 0
          : this.coalesce(value).length === 0,
      securityName: (value) =>
        this.isRFTituloPublico()
          ? this.coalesce(value).length > 0
          : this.coalesce(value).length === 0,
    };
  }

  // Init
  ngOnInit() {
    this.dataChanged = new EventEmitter<string>();
    this.model = new Margin();

    this.parametersInit();
    this.validatorsInit();
    this.modelInit();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.frm.resetForm();
    this.model = new Margin();

    this.fundoComponent.resetForm();
    this.fundoComponent.portfolio = new Portfolio();

    this.annotationComponent.annotation = null;

    this.isEditing = false;
    this.canValidate = false;

    this.parametersInit();
    this.validatorsInit();
    this.modelInit();
    this.model.id = 0;
    this.model.operationDate = new Date();
    this.dataChanged = new EventEmitter<string>();
    this.setEnable(true);
  }

  setEnable(enabled: boolean = false) {
    setTimeout(() => {
      if (enabled) {
        this.fundoComponent.setEnable(true);
        Object.values(this.frm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit()) {
        this.fundoComponent.setEnable(false);
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.frm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  getTicket(idBoleta: number = null) {
    this.modelInit(idBoleta);
  }

  containsGroup(groups: GroupsEnum[]): boolean {
    return this.authService.hasGroup(groups);
  }

  parametersInit() {
    this.TiposMercado = this.service.ListMarketTypes();
    this.TiposCompra = this.service.ListMarginOperationTypes();
    this.domainService.ListBanks().subscribe((b) => {
      this.banksList = b;
    });
    this.service.getPublicSecurityTypes().subscribe((p) => {
      this.publicSecurityTypes = p;
    });
  }

  modelInit(idBoleta: number = null) {
    const id =
      idBoleta === null ? +this.route.snapshot.paramMap.get("id") : idBoleta;

    this.model.id = id;

    if (id) {
      this.service.Margin.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);
          this.handleDataFromRepository();

          this.isEditing = true;
          this.canValidate = true;

          this.setEnable(false);
          this.dataChanged.emit("");
        },
        () => this.router.navigate([MenuConstants.Tickets.Margin])
      );
    } else {
      this.isEditing = false;
      this.model = new Margin();
      this.model.marketTypeId = this.TiposMercado[0].id;
      this.model.operationTypeId = this.TiposCompra[0].id;
      if (!this.model.statusId) {
        this.model.operationDate = new Date();
      }
      // this.mock();
      // this.handleDataFromRepository();
      // this.calculateOperationValue(null);
    }
  }

  submit() {
    this.loaderService.addLoader();

    const editing = this.isEditing;

    this.register(this.frm)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
        },
        () => {}
      );
  }

  register(f: NgForm, duplicate: boolean = false) {
    const editing = this.isEditing;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    try {
      const isValid = this.validateForm(f);
      if (isValid) {
        let data = Object.assign({}, this.model);

        if (data.issueDate != null) {
          data.issueDate = new Date(
            data.issueDate
              .toString()
              .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
          );
        }

        if (data.expirationDate != null) {
          data.expirationDate = new Date(
            data.expirationDate
              .toString()
              .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
          );
        }

        if (data.purchaseDate != null) {
          data.purchaseDate = new Date(
            data.purchaseDate
              .toString()
              .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
          );
        }

        data = this.cleanDataForSubmitting(data);

        return this.sendSubmit(data, editing, duplicate);
      }
    } catch (error) {
      return throwError(() => error.message);
    }
  }

  confirmForward(): Promise<boolean> {
    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    var textConfirm = 'A boleta não poderá mais ser editada, deseja continuar?';

    if(this.isNotProduction) {
      textConfirm += '<br />ATENÇÃO: ESTE É UM AMBIENTE DE TESTE. NÃO EXECUTE OPERAÇÕES REAIS.';
    }

    return this.dialogService
      .confirm(
        "Encaminhar Boleta",
        textConfirm
      )
      .toPromise()
      .then((result) => {
        if (!result) {
          return false;
        }

        const operationDate = DateTime.fromJSDate(
          new Date(this.model.operationDate.toString())
        );

        if (operationDate.day < DateTime.local().day) {
          return this.dialogService
            .confirm(
              "Boleta Retroativa",
              "A data de operação da boleta será alterada para a data de hoje.",
              "OK",
              "Cancelar"
            )
            .toPromise();
        }

        return true;
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }

      this.register(this.frm)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            return throwError(err);
          })
        )
        .subscribe(
          (margin: Margin) => {
            this.service.Margin.forwardTicket(margin.id).subscribe((res) => {
              this.model.statusDescription = res.statusDescription;

              this.setEnable(false);

              this.snackBarService.open("Boleta encaminhada.", "OK", {
                verticalPosition: "top",
                horizontalPosition: "right",
              });

              this.getTicket(margin.id);
            });
          },
          () => {}
        );
    });
  }

  cancel() {
    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.Margin.cancelDraftTicket(this.model.id);
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.frm.controls).forEach((c) => {
            c.disable();
          });

          this.snackBarService.open("Boleta cancelada.", "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.getTicket(this.model.id);
        }
      });
  }

  approve(): void {
    if (this.hasErrorElectronicTransfer()) {
      this.dialogService
        .confirm(
          "Aprovar Boleta",
          "ATENÇÃO: O status da Transferência TED/TEF nesta boleta está com erro. Isto significa que a transferência não aconteceu e a liquidação deve ser feita de forma manual. Deseja aprovar a boleta mesmo assim?",
          "Aprovar mesmo assim",
          "Cancelar",
          DialogSize.Medium
        )
        .subscribe((result) => {
          if (result) {
            this.ticketApprove();
          }
        });
    } else {
      this.dialogService
        .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
        .subscribe((result) => {
          if (result) {
            this.ticketApprove();
          }
        });
    }
  }

  private ticketApprove() {
    this.service.approve(this.model.id, "").subscribe(() => {
      this.snackBarService.open("Boleta atualizada com sucesso.");

      this.getTicket(this.model.id);
    });
  }

  reject() {
    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service
            .reject(this.model.id, result.justification)
            .subscribe(() => {
              this.snackBarService.open(
                "Boleta atualizada com sucesso.",
                "OK",
                {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                }
              );
              this.getTicket(this.model.id);
            });
        }
      });
  }

  duplicate() {
    this.register(this.frm, true).subscribe((newTicket) => {
      this.snackBarService
        .open("Boleta duplicada.", "Visualizar no Monitor", {
          verticalPosition: "top",
          horizontalPosition: "right",
          duration: 6000,
        })
        .onAction()
        .subscribe((a) => {
          this.router.navigate([
            MenuConstants.Todos,
            { ticketId: TicketType.Margin },
          ]);
        });
    });
  }

  // Auxiliares - view
  isRFTituloPrivado() {
    return this.model.marketTypeId === MarketType.FixedIncomePrivateAsset;
  }
  isRFTituloPublico() {
    return this.model.marketTypeId === MarketType.FixedIncomeGovernmentBond;
  }
  isRendaVariavel() {
    return this.model.marketTypeId === MarketType.VariableIncome;
  }
  isDinheiro() {
    return this.model.marketTypeId === MarketType.Cash;
  }
  isRescue(): boolean {
    return this.model.operationTypeId === OperationType.Withdrawal;
  }

  // Auxiliares - validação
  coalesce(value: any) {
    return value || "";
  }

  getValidators() {
    return this.validators;
  }

  hasError(prop: string) {
    if (!this.canValidate) {
      return false;
    }

    return !this.getValidators()[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];
    const validators = this.getValidators();
    Object.keys(validators).forEach((k) => {
      if (!validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  validateForm(f: NgForm): boolean {
    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.markAsDirty();
    });

    this.canValidate = true;

    const errors = this.getErrors();

    this.fundoComponent.validatePortfolioFields();
    const fundErrors = this.fundoComponent.getErrors();
    const counterpartErrors = this.counterpartComponent.getErrors();

    if (f.invalid || errors.length > 0) {
      throw new Error("Verifique os campos destacados.");
    }

    if (fundErrors.length > 0) {
      throw new Error("Verifique os campos nos dados do Fundo Investidor.");
    }

    if (counterpartErrors.length > 0) {
      throw new Error("Verifique os campos nos dados da Contraparte.");
    }

    return true;
  }

  transformDecimal(val: number): number {
    return this.decimalPipe.transform(val, "1.0-8", "pt-BR") as any;
  }

  formatDocument(val: string): string {
    return this.validator.formatDocument(val);
  }

  transformDateForSubmitting(val: Date) {
    if (val != null) {
      return new Date(
        val.toString().replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    } else {
      return null;
    }
  }

  calculateOperationValue(newValue) {
    const amount = this.validator.getCurrency(this.model.amount);
    const unitPrice = this.validator.getCurrency(this.model.unitPrice);
    const operationValue = amount * unitPrice;
    this.model.operationValue = this.transformDecimal(operationValue);
  }

  // Auxiliares - submit
  cleanDataForSubmitting(data: Margin) {
    data.amount = this.validator.getCurrency(data.amount);
    data.operationValue = this.validator.getCurrency(data.operationValue);
    data.unitPrice = this.validator.getCurrency(data.unitPrice);
    data.quotation = this.validator.getCurrency(data.quotation);
    data.counterpartDocument = this.validator.sanitize(
      data.counterpartDocument
    );

    if (this.isRFTituloPrivado()) {
      data.quotation = null;
      data.asset = null;
      data.counterpartBrokerAccount = null;
      data.securityName = null;
      // data.expirationDate = this.transformDateForSubmitting(
      //   data.expirationDate
      // );
      //data.issueDate = this.transformDateForSubmitting(data.issueDate);
      //data.purchaseDate = this.transformDateForSubmitting(data.purchaseDate);
      data.bank = null;
      data.branch = null;
      data.account = null;
    } else if (this.isRFTituloPublico()) {
      data.quotation = null;
      data.asset = null;
      data.counterpartBrokerAccount = null;
      data.securityType = null;
      data.expirationDate = this.transformDateForSubmitting(
        data.expirationDate
      );
      data.purchaseDate = this.transformDateForSubmitting(data.purchaseDate);
      data.bank = null;
      data.branch = null;
      data.account = null;
      data.issuer = null;
    } else if (this.isRendaVariavel()) {
      data.unitPrice = null;
      data.operationValue = null;
      data.securityType = null;
      data.securityCode = null;
      data.expirationDate = null;
      data.purchaseDate = null;
      data.bank = null;
      data.branch = null;
      data.account = null;
      data.issuer = null;
    } else if (this.isDinheiro()) {
      data.quotation = null;
      data.asset = null;
      data.unitPrice = null;
      data.securityType = null;
      data.securityCode = null;
      data.expirationDate = null;
      data.purchaseDate = null;
      data.counterpartClearingAccount = null;
      data.portfolioClearingAccount = null;
      data.counterpartBrokerAccount = null;
      data.issuer = null;
    } else {
      data = null;
    }

    data = this.fundoComponent.fillPortfolioData(
      data,
      this.fundoComponent.portfolio
    );

    data = this.counterpartComponent.fillCounterpartData(
      data,
      this.counterpartComponent.counterpartModel
    );

    data.annotations = this.annotationComponent.annotation;

    return data;
  }

  handleDataFromRepository() {
    if (this.model.operationValue) {
      this.model.operationValue = this.transformDecimal(
        this.model.operationValue
      );
    }
    if (this.model.unitPrice) {
      this.model.unitPrice = this.transformDecimal(this.model.unitPrice);
    }
    if (this.model.quotation) {
      this.model.quotation = this.transformDecimal(this.model.quotation);
    }
    if (this.model.amount) {
      this.model.amount = this.transformDecimal(this.model.amount);
    }
    if (this.model.counterpartDocument) {
      this.model.counterpartDocument = this.formatDocument(
        this.model.counterpartDocument
      );
    }
    if (this.model.portfolioDocument) {
      this.model.portfolioDocument = this.formatDocument(
        this.model.portfolioDocument
      );
    }

    if (this.model.expirationDate) {
      this.model.expirationDate = this.datePipe.transform(
        this.model.expirationDate,
        "yyyy-MM-dd",
        "UTC/GMT"
      ) as any;
    }

    if (this.model.purchaseDate) {
      this.model.purchaseDate = this.datePipe.transform(
        this.model.purchaseDate,
        "yyyy-MM-dd",
        "UTC/GMT"
      ) as any;
    }

    if (this.model.marketTypeId === MarketType.FixedIncomeGovernmentBond) {
      this.selectedPublicSecurity = this.model.securityName;
      this.service
        .getPublicSecurity(this.selectedPublicSecurity)
        .subscribe((res) => {
          this.publicSecurityList = res;

          this.publicSecurityList.forEach((x) => {
            x.expirationDate = this.datePipe.transform(
              x.expirationDate,
              "dd/MM/yyyy",
              "UTC/GMT"
            ) as any;
            x.issueDate = this.datePipe.transform(
              x.issueDate,
              "dd/MM/yyyy",
              "UTC/GMT"
            ) as any;
          });

          this.selectedPublicSecurityItem = this.publicSecurityList.filter(
            (p) =>
              p.expirationDate ===
              (this.datePipe.transform(
                this.model.expirationDate,
                "dd/MM/yyyy",
                "UTC/GMT"
              ) as any)
          )[0];
        });
    }

    this.fundoComponent.fillComponentFieldsLoad(this.model);

    this.counterpartComponent.fillComponentFieldsLoad(this.model);
  }

  sendSubmit(
    data: Margin,
    editing: boolean = false,
    duplicate: boolean = false
  ) {
    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    const method =
      editing && !duplicate
        ? this.service.Margin.updateTicket(this.model.id, data)
        : this.service.Margin.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          return throwError("Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((margin: Margin) => {
          if (!editing) {
            this.model.id = margin.id;
            this.model.operationDate = margin.operationDate;
            this.model.statusId = margin.statusId;
            this.model.accountManagerName = margin.accountManagerName;
            this.model.accountManagerDocument = margin.accountManagerDocument;
            this.model.ticketCutoffTypeId = margin.ticketCutoffTypeId;
            this.model.portfolioFundId = margin.portfolioFundId;

            this.isEditing = true;

            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      );
  }

  canEdit() {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolioFundId;

    if (accountManagerId === null) {
      if (this.model.marketTypeId === MarketType.FixedIncomePrivateAsset) {
        if (this.model.operationTypeId === OperationType.Deposit) {
          cutoffTypeId = TicketCutoffTypeEnum.MarginDepositPrivate;
        } else if (this.model.operationTypeId === OperationType.Withdrawal) {
          cutoffTypeId = TicketCutoffTypeEnum.MarginWithdrawalPrivate;
        }
      } else if (
        this.model.marketTypeId === MarketType.FixedIncomeGovernmentBond
      ) {
        if (this.model.operationTypeId === OperationType.Deposit) {
          cutoffTypeId = TicketCutoffTypeEnum.MarginDepositPublic;
        } else if (this.model.operationTypeId === OperationType.Withdrawal) {
          cutoffTypeId = TicketCutoffTypeEnum.MarginWithdrawalPublic;
        }
      } else if (this.model.marketTypeId === MarketType.VariableIncome) {
        if (this.model.operationTypeId === OperationType.Deposit) {
          cutoffTypeId = TicketCutoffTypeEnum.MarginDepositVariable;
        } else if (this.model.operationTypeId === OperationType.Withdrawal) {
          cutoffTypeId = TicketCutoffTypeEnum.MarginWithdrawalVariable;
        }
      } else if (this.model.marketTypeId === MarketType.Cash) {
        cutoffTypeId = TicketCutoffTypeEnum.MarginMoney;
      }

      accountManagerId = this.authService.authToken.accountManagerId;
      fundId = this.fundoComponent.portfolio.fundId;
    }
    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate() {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateTicketFundQuota) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus() {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  /**
   * @description Check if can send tranfer for liquidation
   * @returns true if workflow status is awaiting Shipping / Receiving of Matera Transfer
   */
  canSendTransferForLiquidation(): boolean {
    return (
      this.model.statusId === 7 &&
      this.authService.hasRole(Roles.ApprovePendingSettlementCustody)
    );
  }

  /**
   * @description Check if can check status of Matera Tranfer
   * @returns true if workflow status is on transfer liquidation
   */
  canCheckStatusTransfer(): boolean {
    return (
      this.model.statusId === 8 &&
      this.authService.hasRole(Roles.ApproveActiveSettlement)
    );
  }

  electronicTransferAvailable(): void {
    if (
      this.isDinheiro() &&
      this.model.operationTypeId === OperationType.Deposit
    ) {
      this.service.Margin.getEletronicTransferMarginTicket(
        this.model.id
      ).subscribe(
        (result) => {
          this.snackBarService.open(`${result.message}`);
        },
        (err) => {
          if (err.status === 404 || err.status === 400) {
            this.snackBarService.open(err.error.message);
          }
        }
      );
    } else {
      this.snackBarService.open("Esta boleta não é do tipo Dinheiro depósito");
    }
  }

  checkEletronicTransferSituation(): void {
    this.service.Margin.getEletronicTransferSituationMarginTicket(
      this.model.id
    ).subscribe(
      (result) => {
        this.snackBarService.open(`${result.message}`);
      },
      (err) => {
        this.snackBarService.open(`Erro: ${err.error.message}`);
      }
    );
  }

  disableActionButtons() {
    let disabled =
      this.model.transferStatusDescription == "Transferência em Trânsito" ||
      this.model.transferStatusDescription == "Transferência Pendente" ||
      this.model.transferStatusDescription == "Transferência Agendada";

    return disabled;
  }

  hasErrorElectronicTransfer() {
    return this.model.transferStatusDescription == "Transferência com Erro";
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }

  isDeposit() {
    return this.model.operationTypeId === OperationType.Deposit;
  }

  publicSecurityTypeSelected() {
    if (this.selectedPublicSecurity) {
      this.model.securityName = this.selectedPublicSecurity;

      this.clearPublicSecurityItem();

      this.service
        .getPublicSecurity(this.selectedPublicSecurity)
        .subscribe((res) => {
          this.publicSecurityList = this.publicSecurityListResult = res;
          const securityIdList = res
            .map((item) => item.securityId)
            .filter((value, index, self) => self.indexOf(value) === index);

          this.publicSecurityIdsList = securityIdList;

          if (securityIdList.length > 1) {
            this.model.securityCode = null;
          } else if (securityIdList.length === 1) {
            this.model.securityCode = securityIdList[0];
          }

          this.publicSecurityList.forEach((x) => {
            x.expirationDate = this.datePipe.transform(
              x.expirationDate,
              "dd/MM/yyyy",
              "UTC/GMT"
            ) as any;
            x.issueDate = this.datePipe.transform(
              x.issueDate,
              "dd/MM/yyyy"
            ) as any;
          });
        });
    } else {
      this.model.securityName = null;
      this.clearPublicSecurityItem();
    }
  }

  publicSecurityIdSelected() {
    this.clearPublicSecurityItem();

    const filteredPublicSecurityList = this.publicSecurityListResult.filter(
      (x) => x.securityId === this.model.securityCode
    );
    this.publicSecurityList = filteredPublicSecurityList;

    if (filteredPublicSecurityList.length === 1) {
      this.selectedPublicSecurityItem = filteredPublicSecurityList[0];
      this.publicSecurityItemSelected();
    }
  }

  publicSecurityItemSelected() {
    if (this.selectedPublicSecurityItem) {
      this.model.securitySourceId =
        this.selectedPublicSecurityItem.securitySourceId;
      this.model.expirationDate =
        this.selectedPublicSecurityItem.expirationDate;
      this.model.issueDate = this.selectedPublicSecurityItem.issueDate;
    } else {
      this.clearPublicSecurityItem();
    }
  }

  clearPublicSecurityItem() {
    this.model.securitySourceId = null;
    this.model.expirationDate = null;
    this.model.issueDate = null;

    this.selectedPublicSecurityItem = null;
  }

  clearPublicSecurityFields() {
    this.model.securityName = null;
    this.model.securityCode = null;
    this.model.expirationDate = null;

    this.publicSecurityIdsList = null;
    this.selectedPublicSecurity = null;

    this.clearPublicSecurityItem();
  }

  // Auxiliares - mock
  mock() {
    this.model.amount = 1;
    this.model.unitPrice = 1;
    this.model.operationValue = 1;
    this.model.securityType = "security type";
    this.model.securityName = "security name";
    this.model.securityCode = "security code";
    this.model.expirationDate = new Date("2019-03-31T00:00:00");
    this.model.purchaseDate = new Date("2019-02-28T00:00:00");
    this.model.counterpartName = "counterpart name";
    this.model.counterpartDocument = "04.405.242/0001-16";
    this.model.counterpartClearingAccount = "123";
    this.model.command = "command";
    this.model.quotation = 1;
    this.model.asset = "asset";
    this.model.counterpartBrokerAccount = "123";
    this.model.portfolioDocument = "04.405.242/0001-16";
    this.model.portfolioAccount = "123";
    this.model.portfolioClearingAccount = "123";
    this.model.portfolioName = "portfolio name";
    this.model.annotations = "annotations";
  }

  clearFields() {
    this.fundoComponent.resetForm();
    this.fundoComponent.portfolio = new Portfolio();

    if (this.isRFTituloPrivado()) {
      this.model.asset = null;
      this.model.counterpartBrokerAccount = null;
      this.selectedPublicSecurity = this.model.securityName = null;
      this.model.quotation = null;

      this.model.bank = null;
      this.model.branch = null;
      this.model.account = null;
    } else if (this.isRFTituloPublico()) {
      this.selectedPublicSecurity = this.model.securityName = null;
      this.model.securityCode = null;
      this.model.securityType = null;
      this.model.expirationDate = null;
      this.model.issuer = null;

      this.model.asset = null;
      this.model.counterpartBrokerAccount = null;

      this.model.bank = null;
      this.model.branch = null;
      this.model.account = null;
      this.model.quotation = null;
    } else if (this.isRendaVariavel()) {
      this.selectedPublicSecurity = this.model.securityName = null;
      this.model.securityCode = null;
      this.model.securityType = null;
      this.model.counterpartClearingAccount = null;
      this.model.expirationDate = null;
      this.model.purchaseDate = null;
      this.model.unitPrice = null;
      this.model.operationValue = null;
      this.model.issuer = null;

      this.model.bank = null;
      this.model.branch = null;
      this.model.account = null;
    } else if (this.isDinheiro()) {
      this.selectedPublicSecurity = this.model.securityName = null;
      this.model.securityCode = null;
      this.model.securityType = null;
      this.model.counterpartClearingAccount = null;
      this.model.expirationDate = null;
      this.model.purchaseDate = null;
      this.model.unitPrice = null;
      this.model.operationValue = null;
      this.model.issuer = null;

      this.model.asset = null;
      this.model.counterpartBrokerAccount = null;
      this.model.quotation = null;
    }
  }

  showTransferStatus() {
    return (
      this.model.id != null &&
      this.model.id != 0 &&
      this.model.transferStatusDescription != null &&
      this.model.transferStatusDescription != ""
    );
  }
}
